import type { StoreSpecial as V3StoreSpecial } from '@iheartjane/data-access';
import type { PriceId } from '@iheartjane/types';

import type { AlgoliaPriceId, AlgoliaProduct } from '@jane/search/types';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct as IoTsMenuProduct } from '@jane/shared/models';
import type {
  MaterializedMenuProduct,
  MenuProduct,
  PendingCartProduct,
  StoreSpecial,
} from '@jane/shared/types';
import {
  WEIGHT_FILTER_VALUES,
  availableWeightsForMenuProduct,
  getDiscountedUnitPrice,
  getUnitPrice,
  isDiscountSupported,
  lowestPriceForMenuProduct,
} from '@jane/shared/util';

import { isSupportedStoreSpecial } from '../../../util';

export const useGetProductPrices = (
  product:
    | AlgoliaProduct
    | PendingCartProduct
    | MenuProduct
    | MaterializedMenuProduct,
  specials: (StoreSpecial | V3StoreSpecial)[],
  highlightDeal?: boolean,
  searchingByWeight?: AlgoliaPriceId[]
) => {
  return (priceId?: AlgoliaPriceId | PriceId) => {
    // AlgoliaPriceId is separated by spaces while PriceId uses _
    let finalPriceId = priceId?.split(' ').join('_') as PriceId;

    const supportedStoreSpecials =
      specials &&
      specials?.filter((special) => isSupportedStoreSpecial(special, true));

    if (searchingByWeight && searchingByWeight.length === 1) {
      finalPriceId = searchingByWeight[0].split(' ').join('_') as PriceId;
    }

    if (searchingByWeight && searchingByWeight.length > 1) {
      const sortedWeights = searchingByWeight.sort(
        (a, b) =>
          WEIGHT_FILTER_VALUES.indexOf(a) - WEIGHT_FILTER_VALUES.indexOf(b)
      );

      const filteredPriceId = sortedWeights.find((weight) =>
        getUnitPrice(
          product as IoTsMenuProduct,
          weight.split(' ').join('_') as PriceId
        )
      );

      if (filteredPriceId) {
        finalPriceId = filteredPriceId.split(' ').join('_') as PriceId;
      }
    }

    if (!finalPriceId) {
      let weights = availableWeightsForMenuProduct(product as IoTsMenuProduct);
      if (highlightDeal) {
        const discountedWeights = weights.filter(
          (weight) =>
            !!getDiscountedUnitPrice(product as IoTsMenuProduct, weight)
        );
        weights = discountedWeights.length > 0 ? discountedWeights : weights;
      }
      finalPriceId =
        lowestPriceForMenuProduct(product as IoTsMenuProduct, weights) ||
        'each';
    }

    const discountInScope = isDiscountSupported(
      product as IoTsMenuProduct,
      finalPriceId,
      supportedStoreSpecials
    );

    const discountPrice = discountInScope
      ? getDiscountedUnitPrice(product as IoTsMenuProduct, finalPriceId)
      : undefined;

    const unitPrice = getUnitPrice(product as IoTsMenuProduct, finalPriceId);

    return {
      discountPrice,
      price: discountPrice || unitPrice,
      priceId: finalPriceId,
      unitPrice,
    };
  };
};
